import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "status", "passwordFields", "everyoneConfirmation", "connectionsConfirmation", "deletionProgress"]

  connect() {
    this.originalData = this._snapshot()
    this.saving = false
    this.saveQueued = false
  }

  disconnect() {
    clearTimeout(this.saveTimer)
    clearTimeout(this.statusTimer)
  }

  deletionStarted() {
    this.deletionProgressTarget.showModal()
  }

  deletionFinished() {
    if (this.deletionProgressTarget.open) this.deletionProgressTarget.close()
  }

  preventDeletionDismiss(event) {
    event.preventDefault()
  }

  changed(event) {
    if (event.target.type === "password") return

    if (event.target.name === "user[matching_audience]") {
      const openingToEveryone = event.target.value === "everyone" &&
        this.originalData["user[matching_audience]"] === "connections_only"
      const limitingToConnections = event.target.value === "connections_only" &&
        this.originalData["user[matching_audience]"] === "everyone"

      if (openingToEveryone && !this.everyoneConfirmed) {
        clearTimeout(this.saveTimer)
        if (!this.everyoneConfirmationTarget.open) this.everyoneConfirmationTarget.showModal()
        return
      }

      if (limitingToConnections && !this.connectionsOnlyConfirmed) {
        clearTimeout(this.saveTimer)
        if (!this.connectionsConfirmationTarget.open) this.connectionsConfirmationTarget.showModal()
        return
      }

      if (event.target.value === "connections_only") this.everyoneConfirmed = false
      if (event.target.value === "everyone") this.connectionsOnlyConfirmed = false
    }

    const delay = event.type === "change" ? 350 : 700
    this._scheduleSave(delay)
  }

  confirmEveryone() {
    this.everyoneConfirmed = true
    this.everyoneConfirmationTarget.close()
    this._scheduleSave(0)
  }

  cancelEveryone(event) {
    event.preventDefault()
    const connectionsOnly = this.formTarget.querySelector('input[name="user[matching_audience]"][value="connections_only"]')
    if (connectionsOnly) connectionsOnly.checked = true
    this.everyoneConfirmed = false
    if (this.everyoneConfirmationTarget.open) this.everyoneConfirmationTarget.close()
  }

  confirmConnectionsOnly() {
    this.connectionsOnlyConfirmed = true
    this.connectionsConfirmationTarget.close()
    this._scheduleSave(0)
  }

  cancelConnectionsOnly(event) {
    event.preventDefault()
    const everyone = this.formTarget.querySelector('input[name="user[matching_audience]"][value="everyone"]')
    if (everyone) everyone.checked = true
    this.connectionsOnlyConfirmed = false
    if (this.connectionsConfirmationTarget.open) this.connectionsConfirmationTarget.close()
  }

  togglePassword(event) {
    const hidden = this.passwordFieldsTarget.hidden
    this.passwordFieldsTarget.hidden = !hidden
    event.currentTarget.setAttribute("aria-expanded", hidden.toString())
    if (hidden) this.passwordFieldsTarget.querySelector("input")?.focus()
  }

  _snapshot() {
    const data = {}
    for (const [key, value] of new FormData(this.formTarget).entries()) {
      if (key === "user[password]" || key === "user[password_confirmation]") continue
      data[key] = value
    }
    return data
  }

  _isDirty() {
    for (const [key, value] of new FormData(this.formTarget).entries()) {
      if (key === "user[password]" || key === "user[password_confirmation]") continue
      if (value !== (this.originalData[key] ?? "")) return true
    }
    return false
  }

  async _save() {
    if (!this._isDirty()) return

    if (this.saving) {
      this.saveQueued = true
      return
    }

    this.saving = true
    this.saveQueued = false
    this._setStatus("Saving…")

    const submittedData = this._snapshot()
    const formData = new FormData(this.formTarget)
    formData.delete("user[password]")
    formData.delete("user[password_confirmation]")

    try {
      const response = await fetch(this.formTarget.action, {
        method: this.formTarget.method,
        body: formData,
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })

      if (!response.ok) {
        const result = await response.json().catch(() => ({}))
        throw new Error(result.errors?.join(", ") || "Couldn’t save")
      }

      this.originalData = submittedData
      this._setStatus("Saved", 1600)
    } catch (error) {
      this._setStatus(error.message || "Couldn’t save", 3000)
    } finally {
      this.saving = false

      if (this.saveQueued) {
        this.saveQueued = false
        this._save()
      }
    }
  }

  _scheduleSave(delay) {
    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => this._save(), delay)
  }

  _setStatus(message, clearAfter = null) {
    clearTimeout(this.statusTimer)
    this.statusTarget.textContent = message

    if (clearAfter) {
      this.statusTimer = setTimeout(() => {
        this.statusTarget.textContent = ""
      }, clearAfter)
    }
  }
}
